import { SxProps, Theme, TooltipProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useCallback } from 'react';

import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

const OUTLINE_WIDTH_IN_PX = 1;

export type StatusTab<T> = {
  badgeProps?: BadgeProps;
  label: string | React.ReactNode;
  value: T;
};

interface StatusTabsProps<T> {
  onChange: (value: T) => void;
  selectedTab: T;
  sx?: SxProps;
  tabs: StatusTab<T>[];
}

export default function StatusTabs<T>({
  onChange,
  selectedTab,
  sx,
  tabs,
}: StatusTabsProps<T>) {
  const handleTabChange = useCallback(
    (value: T) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <Box
      sx={[
        {
          alignItems: 'center',
          display: 'flex',
          height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX - OUTLINE_WIDTH_IN_PX * 2,
          position: 'relative',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {tabs.map((tab, index) => (
        <Tab
          key={String(tab.value)}
          {...tab}
          isFirstTab={index === 0}
          isLastTab={index === tabs.length - 1}
          isSelected={selectedTab === tab.value}
          onClick={handleTabChange}
        />
      ))}
    </Box>
  );
}

interface TabProps<T> extends StatusTab<T> {
  isFirstTab?: boolean;
  isLastTab?: boolean;
  isSelected?: boolean;
  onClick: StatusTabsProps<T>['onChange'];
}

function Tab<T>({
  badgeProps,
  isFirstTab,
  isLastTab,
  isSelected,
  label,
  onClick,
  value,
}: TabProps<T>) {
  const theme = useTheme();

  return (
    <Box
      onClick={() => onClick(value)}
      sx={{
        alignItems: 'center',
        bgcolor: isSelected
          ? theme.brandV2.colors.treadGray1
          : theme.semantic.surfaceRaised,
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        p: theme.spacing(0.5, 1),
        position: 'relative',
        cursor: 'pointer',
        mr: isSelected ? '-1px' : 0,
        outline: `solid ${OUTLINE_WIDTH_IN_PX}px ${isSelected ? theme.brandV2.colors.treadGray1 : theme.brandV2.colors.treadGray7}`,
        zIndex: isSelected ? 1 : 0,

        ...(isFirstTab
          ? {
              borderBottomLeftRadius: theme.brandV2.borderRadius,
              borderTopLeftRadius: theme.brandV2.borderRadius,
              pl: theme.spacing(1.5),
            }
          : { ml: '1px' }),

        ...(isLastTab
          ? {
              borderBottomRightRadius: theme.brandV2.borderRadius,
              borderTopRightRadius: theme.brandV2.borderRadius,
              pr: theme.spacing(1.5),
            }
          : {}),
      }}
    >
      <Typography
        component="span"
        variant="subtitle2"
        sx={{
          alignItems: 'center',
          color: isSelected
            ? theme.semantic.textInverse
            : theme.brandV2.colors.treadBlack,
          display: 'flex',
          gap: 0.75,
          whiteSpace: 'nowrap',
        }}
      >
        {label}

        {badgeProps && <Badge {...badgeProps} />}
      </Typography>
    </Box>
  );
}

interface BadgeTooltipProps {
  placement?: TooltipProps['placement'];
  title: React.ReactNode;
}

interface BadgeProps {
  content: string;
  sx?: SxProps<Theme>;
  tooltipProps?: BadgeTooltipProps;
}

export function Badge({ content, sx, tooltipProps }: BadgeProps) {
  const Tooltip = ({
    children,
    placement,
    title,
  }: React.PropsWithChildren<BadgeTooltipProps>) => (
    <MuiTooltip placement={placement} title={title}>
      <span>{children}</span>
    </MuiTooltip>
  );

  const badge = (
    <Typography
      component="span"
      variant="subtitle2"
      sx={[
        (theme) => ({
          bgcolor: theme.brandV2.colors.treadGray8,
          color: theme.brandV2.colors.treadBlack,
          fontWeight: 500,
          minWidth: '20px',
          height: '20px',
          lineHeight: 1,
          px: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '10px',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {content}
    </Typography>
  );

  if (tooltipProps) {
    return <Tooltip {...tooltipProps}>{badge}</Tooltip>;
  }

  return badge;
}
